export interface EncyclopediaArticle {
  id: string
  title: string
  category: 'basics' | 'advanced' | 'services'
  summary: string
  sections: { heading: string; text: string }[]
  keyFacts: string[]
  commonMyths: string[]
  tip: string
  relatedService?: string
  relatedServiceLabel?: string
}

export const ENCYCLOPEDIA_ARTICLES: EncyclopediaArticle[] = [
  // ═══════════════════════════════════════════
  // ОСНОВЫ АСТРОЛОГИИ
  // ═══════════════════════════════════════════
  {
    id: 'what-is-astrology',
    title: 'Что такое астрология',
    category: 'basics',
    summary:
      'Полный гид по древнейшей системе знаний о связи космоса и человека. Как работает астрология, на чём она основана и почему остаётся актуальной в XXI веке.',
    sections: [
      {
        heading: 'Определение и суть',
        text: 'Астрология — это система знаний, изучающая связь между положением небесных тел (планет, Солнца, Луны) и событиями на Земле, включая характер и судьбу человека. Это не гадание и не религия — это язык символов, который позволяет описать энергетические паттерны, действующие в момент рождения человека и на протяжении всей его жизни. Астрология работает с архетипами — универсальными образами, которые проявляются через планеты, знаки и дома. Каждый элемент натальной карты — это символ определённой энергии, потребности или жизненной темы.',
      },
      {
        heading: 'Краткая история',
        text: 'Астрология зародилась в Месопотамии более 4000 лет назад. Вавилонские жрецы наблюдали за движением планет и связывали их с событиями в государстве. Позже астрология пришла в Египет, Грецию и Рим. Птолемей (II век н.э.) систематизировал астрологические знания в труде «Тетрабиблос», который остаётся фундаментом западной астрологии. В Средние века астрологию преподавали в университетах наравне с медициной и математикой. Кеплер, Галилей и Ньютон занимались астрологией. В XX веке произошло возрождение астрологии благодаря работам Дейна Радьяра, который соединил её с юнгианской психологией.',
      },
      {
        heading: 'Как это работает',
        text: 'Астрология основана на принципе «как вверху, так и внизу» (герметический принцип). Она не утверждает, что планеты физически влияют на нас — скорее, положение планет в момент рождения отражает энергетический «слепок», с которым человек приходит в мир. Натальная карта — это карта неба в момент вашего рождения, спроецированная на плоскость. Она показывает, где находились все планеты, в каких знаках зодиака и в каких домах (сферах жизни). Взаимодействие этих элементов создаёт уникальный рисунок вашей личности.',
      },
      {
        heading: 'Виды астрологии',
        text: 'Существует несколько основных направлений. Натальная астрология изучает карту рождения и характер человека. Прогностическая астрология (транзиты, прогрессии, дирекции) предсказывает периоды и тенденции. Синастрия анализирует совместимость двух людей. Хорарная астрология отвечает на конкретные вопросы по карте момента вопроса. Элективная астрология помогает выбрать лучшее время для важных дел. Мунданная астрология изучает судьбы стран и народов. Медицинская астрология связывает планеты с органами и болезнями.',
      },
      {
        heading: 'Астрология и наука',
        text: 'Астрология не является наукой в современном понимании — она не проходит проверку методом двойного слепого эксперимента. Однако она является мощным инструментом самопознания и психологической работы. Многие практикующие психологи используют натальную карту как дополнительный инструмент диагностики. Карл Густав Юнг активно применял астрологию в своей практике и говорил: «Астрология представляет собой квинтэссенцию всех психологических знаний древности».',
      },
    ],
    keyFacts: [
      'Астрологии более 4000 лет — это одна из древнейших систем знаний',
      'Натальная карта строится на точное время, дату и место рождения',
      'Существует более 10 направлений астрологии: от натальной до медицинской',
      'Астрология работает с архетипами и символами, а не с физическим влиянием планет',
      'Юнг, Кеплер и многие учёные занимались астрологией',
    ],
    commonMyths: [
      '«Астрология — это гороскопы в газетах» — нет, солнечные гороскопы учитывают лишь 1 из 50+ факторов карты',
      '«Планеты управляют нами» — астрология описывает корреляции, а не причинно-следственные связи',
      '«Все Овны одинаковые» — знак Солнца это лишь 5% карты, двух одинаковых карт не существует',
    ],
    tip: 'Начните изучение астрологии с собственной натальной карты. Это самый мотивирующий и практичный способ — вы сразу видите, как символы работают на вашем примере. Закажите расчёт карты и читайте трактовки каждого положения.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Рассчитать свою натальную карту',
  },
  {
    id: 'what-is-natal-chart',
    title: 'Что такое натальная карта',
    category: 'basics',
    summary:
      'Натальная карта (гороскоп рождения) — это главный инструмент астрологии. Узнайте, из чего она состоит, как её читать и зачем она нужна.',
    sections: [
      {
        heading: 'Определение',
        text: 'Натальная карта (от лат. natalis — «относящийся к рождению») — это схема расположения всех планет Солнечной системы в момент вашего рождения, спроецированная на зодиакальный круг. Это ваш астрологический «паспорт», уникальный отпечаток космоса, который определяет ваш характер, таланты, слабости и жизненные темы. Для построения карты необходимы три параметра: точная дата рождения, точное время рождения (желательно до минуты) и место рождения (город).',
      },
      {
        heading: 'Из чего состоит натальная карта',
        text: 'Карта включает несколько ключевых элементов. Планеты (Солнце, Луна, Меркурий, Венера, Марс, Юпитер, Сатурн, Уран, Нептун, Плутон) — это «актёры», каждый из которых отвечает за определённую функцию психики. Знаки зодиака (12 знаков) — это «костюмы» планет, определяющие стиль их проявления. Дома (12 домов) — это «сцены», сферы жизни, где планеты действуют. Аспекты — это углы между планетами, показывающие, как они взаимодействуют друг с другом (гармонично или напряжённо).',
      },
      {
        heading: 'Как читать натальную карту',
        text: 'Чтение карты — это искусство синтеза. Начинают обычно с «большой тройки»: Солнце (ваше ядро, эго, жизненная цель), Луна (эмоции, подсознание, потребности) и Асцендент (внешнее поведение, первое впечатление). Затем анализируют положение каждой планеты в знаке и доме, аспекты между планетами, управителей домов и особые конфигурации (тау-квадрат, большой трин, йод и др.). Профессиональный астролог учитывает десятки факторов и синтезирует их в целостную картину.',
      },
      {
        heading: 'Зачем нужна натальная карта',
        text: 'Натальная карта помогает понять себя на глубинном уровне: почему вы реагируете определённым образом, какие у вас врождённые таланты, в каких сферах жизни вас ждут вызовы, а в каких — удача. Она показывает ваш потенциал и помогает принимать осознанные решения. Карта не предсказывает конкретные события — она описывает энергии и тенденции, с которыми вы работаете. Это инструмент самопознания, а не фатализма.',
      },
      {
        heading: 'Важность точного времени рождения',
        text: 'Время рождения критически важно для точности карты. Асцендент меняется каждые 2 часа, а Луна проходит один знак за 2,5 дня. Разница в 15 минут может изменить положение домов и дать совершенно другую интерпретацию. Если вы не знаете точного времени, его можно уточнить методом ректификации — астролог анализирует ключевые события вашей жизни и восстанавливает время рождения. Без времени можно построить космограмму (карту без домов), но она будет менее информативной.',
      },
    ],
    keyFacts: [
      'Для карты нужны точная дата, время и место рождения',
      'Карта содержит 10 планет, 12 знаков, 12 домов и десятки аспектов',
      '«Большая тройка» — Солнце, Луна, Асцендент — основа интерпретации',
      'Двух одинаковых натальных карт не существует (карта меняется каждые 4 минуты)',
      'Натальная карта — это потенциал, а не приговор',
    ],
    commonMyths: [
      '«Достаточно знать знак зодиака» — знак Солнца это лишь один из 50+ факторов карты',
      '«Карта предсказывает судьбу» — она показывает потенциал и тенденции, а не фиксированные события',
      '«Близнецы имеют одинаковую карту» — даже разница в несколько минут меняет карту',
    ],
    tip: 'Закажите расчёт натальной карты с полным отчётом — вы получите детальный разбор всех положений планет, домов и аспектов с конкретными рекомендациями для вашей жизни.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Заказать полный отчёт по натальной карте',
  },
  {
    id: 'what-is-ascendant',
    title: 'Что такое Асцендент',
    category: 'basics',
    summary:
      'Асцендент (восходящий знак) — один из важнейших элементов натальной карты. Он определяет вашу внешность, поведение и то, как вас воспринимают окружающие.',
    sections: [
      {
        heading: 'Определение Асцендента',
        text: 'Асцендент (ASC) — это знак зодиака, который восходил на восточном горизонте в момент вашего рождения. Он является куспидом (началом) первого дома натальной карты и считается одним из трёх важнейших элементов карты наряду с Солнцем и Луной. Асцендент меняется примерно каждые 2 часа, поэтому для его определения необходимо знать точное время рождения. Именно Асцендент делает каждую карту уникальной — даже люди, рождённые в один день, будут иметь разный Асцендент, если родились в разное время.',
      },
      {
        heading: 'Что показывает Асцендент',
        text: 'Асцендент — это ваша «социальная маска», первое впечатление, которое вы производите на окружающих. Если Солнце — это то, кем вы являетесь внутри, то Асцендент — это то, как вы выглядите снаружи. Он влияет на вашу внешность, телосложение, манеру одеваться и держаться. Он определяет вашу спонтанную реакцию на новые ситуации — как вы входите в комнату, как начинаете разговор, как реагируете на неожиданности. Асцендент также задаёт всю систему домов карты, определяя, какие сферы жизни будут для вас ключевыми.',
      },
      {
        heading: 'Асцендент vs Солнечный знак',
        text: 'Многие люди знают только свой солнечный знак (например, «я Овен»). Но Асцендент часто описывает человека точнее, особенно при первом знакомстве. Солнечный знак — это ваше ядро, ваша глубинная суть, которая раскрывается со временем. Асцендент — это ваш фасад, ваша «упаковка», которую видят все. Например, человек с Солнцем в Раке (мягкий, домашний) и Асцендентом в Овне (энергичный, напористый) будет казаться окружающим гораздо более решительным, чем он чувствует себя внутри.',
      },
      {
        heading: 'Управитель Асцендента',
        text: 'Планета, управляющая знаком Асцендента, называется управителем карты (или управителем гороскопа). Это одна из самых важных планет в карте. Её положение в знаке и доме показывает, куда направлена ваша жизненная энергия. Например, если ваш Асцендент в Тельце, управитель — Венера. Если Венера стоит в 10 доме, ваша жизненная энергия направлена на карьеру, и вы можете добиться успеха в сферах, связанных с красотой, искусством или финансами.',
      },
    ],
    keyFacts: [
      'Асцендент меняется каждые ~2 часа — нужно точное время рождения',
      'Это ваше «первое впечатление» и внешнее поведение',
      'Управитель Асцендента — одна из важнейших планет карты',
      'Асцендент определяет всю систему домов натальной карты',
      'Часто описывает человека точнее, чем солнечный знак',
    ],
    commonMyths: [
      '«Асцендент — это маска, а не настоящий я» — Асцендент это реальная часть вашей личности, не притворство',
      '«Асцендент важнее Солнца» — они равноценны, просто отвечают за разные вещи',
      '«Можно определить Асцендент без времени рождения» — нет, время критически важно',
    ],
    tip: 'Если вы не знаете свой Асцендент — закажите расчёт натальной карты. Это изменит ваше понимание себя. Многие люди говорят: «Наконец-то я понял, почему меня описывают не так, как мой солнечный знак!»',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнать свой Асцендент',
  },
  {
    id: 'zodiac-signs-overview',
    title: 'Знаки зодиака: полный гид',
    category: 'basics',
    summary:
      '12 знаков зодиака — это 12 архетипов энергии. Узнайте о стихиях, крестах, управителях и ключевых качествах каждого знака.',
    sections: [
      {
        heading: 'Что такое знаки зодиака',
        text: 'Знаки зодиака — это 12 равных секторов по 30° на эклиптике (видимом пути Солнца по небу). Каждый знак обладает уникальным набором качеств, энергий и архетипов. Важно понимать: знаки — это не созвездия. Из-за прецессии земной оси тропический зодиак (используемый в западной астрологии) и сидерический (звёздный) зодиак разошлись примерно на 24°. Западная астрология работает с тропическим зодиаком, привязанным к точкам равноденствий и солнцестояний.',
      },
      {
        heading: 'Четыре стихии',
        text: 'Знаки делятся на 4 стихии по 3 знака. Огонь (Овен, Лев, Стрелец) — энергия, действие, энтузиазм, лидерство. Огненные знаки инициативны, страстны и стремятся к самовыражению. Земля (Телец, Дева, Козерог) — стабильность, практичность, материальность. Земные знаки надёжны, трудолюбивы и ценят конкретные результаты. Воздух (Близнецы, Весы, Водолей) — интеллект, общение, идеи. Воздушные знаки общительны, любознательны и мыслят абстрактно. Вода (Рак, Скорпион, Рыбы) — эмоции, интуиция, глубина. Водные знаки чувствительны, эмпатичны и живут в мире переживаний.',
      },
      {
        heading: 'Три креста (модальности)',
        text: 'Каждая стихия представлена в трёх модальностях. Кардинальные знаки (Овен, Рак, Весы, Козерог) — инициаторы, начинают новые циклы, любят действовать. Фиксированные знаки (Телец, Лев, Скорпион, Водолей) — стабилизаторы, упорны, доводят дела до конца, но сопротивляются переменам. Мутабельные знаки (Близнецы, Дева, Стрелец, Рыбы) — адаптаторы, гибки, легко приспосабливаются, но могут быть непостоянны.',
      },
      {
        heading: '12 знаков: краткий обзор',
        text: 'Овен (21.03–19.04) — первопроходец, лидер, воин. Телец (20.04–20.05) — строитель, ценитель, хранитель. Близнецы (21.05–20.06) — коммуникатор, учёный, посредник. Рак (21.06–22.07) — хранитель очага, эмпат, мать. Лев (23.07–22.08) — творец, царь, артист. Дева (23.08–22.09) — аналитик, целитель, мастер. Весы (23.09–22.10) — дипломат, эстет, партнёр. Скорпион (23.10–21.11) — трансформатор, детектив, маг. Стрелец (22.11–21.12) — философ, путешественник, учитель. Козерог (22.12–19.01) — стратег, руководитель, строитель. Водолей (20.01–18.02) — реформатор, гений, гуманист. Рыбы (19.02–20.03) — мистик, художник, целитель.',
      },
    ],
    keyFacts: [
      '12 знаков = 4 стихии × 3 модальности',
      'Западная астрология использует тропический зодиак (привязан к равноденствиям)',
      'Каждый знак занимает ровно 30° зодиакального круга',
      'У каждого знака есть планета-управитель, определяющая его характер',
      'В карте человека задействованы ВСЕ 12 знаков — вопрос лишь в том, какие планеты в них стоят',
    ],
    commonMyths: [
      '«Я типичный Овен» — в карте 10 планет, и каждая в своём знаке. Вы не один знак',
      '«Знаки зодиака совпадают с созвездиями» — нет, тропический зодиак отличается от созвездий',
      '«Некоторые знаки лучше других» — каждый знак имеет свои сильные стороны и вызовы',
    ],
    tip: 'Не ограничивайтесь солнечным знаком. Узнайте, в каких знаках стоят ваша Луна, Венера, Марс — это даст гораздо более полную картину вашей личности.',
    relatedService: 'free-chart',
    relatedServiceLabel: 'Рассчитать карту бесплатно',
  },
  {
    id: 'planets-in-astrology',
    title: 'Планеты в астрологии',
    category: 'basics',
    summary:
      '10 планет — это 10 ключевых функций вашей психики. Каждая планета отвечает за определённую потребность, энергию и сферу жизни.',
    sections: [
      {
        heading: 'Роль планет в карте',
        text: 'Планеты — это главные «действующие лица» натальной карты. Каждая планета символизирует определённую психическую функцию, потребность или тип энергии. Знак, в котором стоит планета, показывает стиль её проявления. Дом показывает сферу жизни, где эта энергия реализуется. Аспекты показывают, как планеты взаимодействуют друг с другом.',
      },
      {
        heading: 'Личные планеты (быстрые)',
        text: 'Солнце — ваше ядро, эго, жизненная цель, отец. Отвечает за то, КЕМ вы являетесь. Луна — эмоции, подсознание, привычки, мать. Отвечает за то, ЧТО вы чувствуете. Меркурий — мышление, речь, обучение, коммуникация. Отвечает за то, КАК вы думаете. Венера — любовь, красота, ценности, деньги. Отвечает за то, ЧТО вы любите. Марс — действие, энергия, агрессия, сексуальность. Отвечает за то, КАК вы действуете. Эти планеты движутся быстро и определяют индивидуальные черты характера.',
      },
      {
        heading: 'Социальные планеты',
        text: 'Юпитер — расширение, удача, мировоззрение, высшее образование. Цикл ~12 лет. Показывает, где вам везёт и как вы растёте. Сатурн — ограничения, дисциплина, карьера, ответственность. Цикл ~29 лет. Показывает, где вы должны упорно трудиться. Юпитер и Сатурн формируют ваше отношение к обществу, карьере и жизненным целям. Их положение в знаке общее для людей одного возраста (±1–2 года).',
      },
      {
        heading: 'Высшие (трансперсональные) планеты',
        text: 'Уран — свобода, революция, оригинальность, внезапные перемены. Цикл ~84 года. Нептун — духовность, иллюзии, творчество, растворение границ. Цикл ~165 лет. Плутон — трансформация, власть, смерть и возрождение, глубинные кризисы. Цикл ~248 лет. Эти планеты движутся очень медленно и определяют характер целых поколений. Их влияние на личность проявляется через дом и аспекты к личным планетам.',
      },
    ],
    keyFacts: [
      'Солнце и Луна — «светила», основа личности',
      'Личные планеты (Солнце–Марс) определяют индивидуальный характер',
      'Социальные планеты (Юпитер, Сатурн) связаны с карьерой и обществом',
      'Высшие планеты (Уран, Нептун, Плутон) определяют поколенческие темы',
      'Каждая планета управляет одним или двумя знаками зодиака',
    ],
    commonMyths: [
      '«Плутон больше не планета» — в астрологии Плутон остаётся одной из важнейших планет',
      '«Солнце и Луна — не планеты» — в астрологии их называют «светилами», но включают в список планет',
      '«Все планеты одинаково важны» — личные планеты влияют на характер сильнее, чем высшие',
    ],
    tip: 'Обратите особое внимание на планету-управитель вашего Асцендента — это «хозяин» вашей карты, и её положение показывает главное направление вашей жизни.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнать положение всех планет в вашей карте',
  },
  {
    id: 'houses-in-astrology',
    title: 'Дома в астрологии',
    category: 'basics',
    summary:
      '12 домов натальной карты — это 12 сфер вашей жизни. От денег и карьеры до любви и духовности — каждый дом отвечает за свою область.',
    sections: [
      {
        heading: 'Что такое дома',
        text: 'Дома — это 12 секторов натальной карты, каждый из которых отвечает за определённую сферу жизни. Если знаки показывают КАК проявляется энергия, то дома показывают ГДЕ она реализуется. Система домов рассчитывается на основе точного времени и места рождения. Начало каждого дома называется куспидом. Куспид первого дома — это Асцендент.',
      },
      {
        heading: 'Угловые дома (1, 4, 7, 10)',
        text: 'Это самые мощные дома карты. 1 дом (Асцендент) — ваша личность, внешность, «я». 4 дом (IC, Имум Цели) — дом, семья, корни, внутренний мир. 7 дом (Десцендент) — партнёрство, брак, «другой». 10 дом (MC, Середина Неба) — карьера, статус, призвание. Планеты в угловых домах проявляются наиболее ярко и заметно.',
      },
      {
        heading: 'Последующие дома (2, 5, 8, 11)',
        text: '2 дом — деньги, ценности, самооценка, ресурсы. 5 дом — творчество, дети, романтика, хобби, удовольствия. 8 дом — кризисы, трансформация, чужие деньги, наследство, секс, смерть. 11 дом — друзья, коллективы, надежды, социальные сети. Эти дома связаны с ресурсами и тем, что вы накапливаете и трансформируете.',
      },
      {
        heading: 'Падающие дома (3, 6, 9, 12)',
        text: '3 дом — общение, братья/сёстры, короткие поездки, начальное образование. 6 дом — работа, здоровье, рутина, служение, домашние животные. 9 дом — философия, дальние путешествия, высшее образование, религия. 12 дом — подсознание, уединение, тайны, кармические долги, духовность. Планеты в падающих домах действуют менее заметно, но не менее важно.',
      },
    ],
    keyFacts: [
      'Для расчёта домов необходимо точное время рождения',
      'Угловые дома (1, 4, 7, 10) — самые мощные',
      'Пустой дом не означает, что сфера жизни неактивна — смотрите управителя',
      'Существует более 20 систем домов (Плацидус, Кох, равнодомная и др.)',
      'Куспид 1 дома = Асцендент, куспид 10 дома = MC (Середина Неба)',
    ],
    commonMyths: [
      '«Пустой дом — значит, ничего не будет в этой сфере» — нет, нужно смотреть управителя дома',
      '«Все системы домов дают одинаковый результат» — нет, результаты могут отличаться',
      '«12 дом — это всегда плохо» — 12 дом связан с духовностью и творчеством, не только с проблемами',
    ],
    tip: 'Обратите внимание на дома, в которых скопилось больше всего планет — это ваши ключевые сферы жизни, где сосредоточена основная энергия.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнать расположение планет по домам',
  },
  {
    id: 'aspects-in-astrology',
    title: 'Аспекты в астрологии',
    category: 'basics',
    summary:
      'Аспекты — это углы между планетами, определяющие, как они взаимодействуют. Гармоничные аспекты дают таланты, напряжённые — мотивацию к росту.',
    sections: [
      {
        heading: 'Что такое аспекты',
        text: 'Аспекты — это угловые расстояния между планетами в натальной карте. Когда две планеты находятся под определённым углом друг к другу, между ними возникает энергетическая связь. Эта связь может быть гармоничной (планеты помогают друг другу) или напряжённой (планеты конфликтуют). Аспекты — это «провода», соединяющие разные части вашей личности. Без аспектов планеты действовали бы изолированно.',
      },
      {
        heading: 'Мажорные аспекты',
        text: 'Соединение (0°) — слияние энергий двух планет. Самый мощный аспект. Планеты усиливают друг друга, но могут и мешать. Секстиль (60°) — мягкая гармония, возможности, которые нужно активировать. Квадратура (90°) — напряжение, конфликт, но и мощный двигатель развития. Тригон (120°) — естественная гармония, врождённые таланты, но риск лени. Оппозиция (180°) — полярность, необходимость баланса, проекции на других.',
      },
      {
        heading: 'Гармоничные vs напряжённые',
        text: 'Гармоничные аспекты (тригон, секстиль) дают таланты и лёгкость, но могут приводить к лени — зачем стараться, если и так получается? Напряжённые аспекты (квадратура, оппозиция) создают внутреннее трение, но именно они заставляют нас расти, преодолевать и достигать. Самые успешные люди часто имеют много квадратур — это их «топливо». Идеальная карта содержит баланс гармоничных и напряжённых аспектов.',
      },
      {
        heading: 'Орбис и точность',
        text: 'Аспект не обязан быть точным до градуса. Допустимое отклонение называется орбисом. Для соединения орбис обычно 8–10°, для тригона и квадратуры — 6–8°, для секстиля — 4–6°. Чем точнее аспект (меньше орбис), тем сильнее он проявляется. Точные аспекты (до 1°) — самые мощные в карте. Аспекты с участием Солнца и Луны допускают больший орбис, чем аспекты между медленными планетами.',
      },
    ],
    keyFacts: [
      '5 мажорных аспектов: соединение, секстиль, квадратура, тригон, оппозиция',
      'Квадратуры — главный двигатель развития личности',
      'Тригоны дают таланты, но могут приводить к лени',
      'Точные аспекты (орбис до 1°) — самые мощные',
      'Соединение — самый сильный аспект, слияние двух энергий',
    ],
    commonMyths: [
      '«Квадратуры — это плохо» — нет, это мотивация и двигатель роста',
      '«Чем больше тригонов, тем лучше» — избыток гармонии может привести к лени',
      '«Нет аспектов — планета не работает» — неаспектированная планета работает непредсказуемо и ярко',
    ],
    tip: 'Найдите в своей карте самый точный аспект — это одна из ключевых тем вашей жизни. Если это квадратура — не бойтесь, именно она даст вам силу для достижений.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнать аспекты в своей карте',
  },
  {
    id: 'elements-and-modalities',
    title: 'Стихии и кресты качеств',
    category: 'basics',
    summary:
      'Стихии (Огонь, Земля, Воздух, Вода) и модальности (Кардинальная, Фиксированная, Мутабельная) — базовая классификация энергий в астрологии.',
    sections: [
      {
        heading: 'Четыре стихии',
        text: 'Огонь (Овен, Лев, Стрелец) — энергия действия, вдохновения и самовыражения. Огненные люди инициативны, страстны, оптимистичны. Им нужно движение и цель. Земля (Телец, Дева, Козерог) — энергия стабильности, практичности и материализации. Земные люди надёжны, трудолюбивы, ценят конкретные результаты. Воздух (Близнецы, Весы, Водолей) — энергия мысли, общения и связей. Воздушные люди интеллектуальны, общительны, любят идеи. Вода (Рак, Скорпион, Рыбы) — энергия чувств, интуиции и глубины. Водные люди эмпатичны, чувствительны, живут эмоциями.',
      },
      {
        heading: 'Баланс стихий в карте',
        text: 'В идеальной карте все стихии представлены равномерно, но на практике это редкость. Преобладание одной стихии усиливает её качества. Недостаток стихии создаёт «слепое пятно». Например, человек без Воды может плохо понимать свои эмоции. Человек без Земли может быть непрактичным. Баланс стихий — один из первых шагов в анализе карты.',
      },
      {
        heading: 'Три модальности (креста)',
        text: 'Кардинальные знаки (Овен, Рак, Весы, Козерог) — инициаторы. Они начинают новые циклы, любят действовать, но могут не доводить дела до конца. Фиксированные знаки (Телец, Лев, Скорпион, Водолей) — стабилизаторы. Они упорны, последовательны, но сопротивляются переменам. Мутабельные знаки (Близнецы, Дева, Стрелец, Рыбы) — адаптаторы. Они гибки и приспособляемы, но могут быть непостоянны.',
      },
    ],
    keyFacts: [
      '4 стихии × 3 модальности = 12 знаков зодиака',
      'Преобладающая стихия определяет ваш базовый темперамент',
      'Недостаток стихии — «слепое пятно», которое нужно компенсировать',
      'Кардинальные знаки начинают, фиксированные продолжают, мутабельные завершают',
      'Баланс стихий и модальностей — первый шаг анализа карты',
    ],
    commonMyths: [
      '«Огненные знаки всегда агрессивны» — огонь это энтузиазм и вдохновение, не только агрессия',
      '«Водные знаки слабые» — вода это глубочайшая сила, способная разрушить скалы',
      '«Воздушные знаки поверхностны» — воздух это интеллект и способность видеть связи',
    ],
    tip: 'Посчитайте, сколько планет в каждой стихии в вашей карте. Преобладающая стихия — ваш базовый темперамент. Недостающая стихия — зона роста.',
  },
  {
    id: 'retrograde-planets',
    title: 'Ретроградные планеты',
    category: 'basics',
    summary:
      'Что значит «Меркурий ретроградный» и стоит ли бояться? Полный разбор ретроградности в натальной карте и транзитах.',
    sections: [
      {
        heading: 'Что такое ретроградность',
        text: 'Ретроградное движение — это оптическая иллюзия, при которой планета кажется движущейся назад по зодиаку. На самом деле планеты не меняют направление — это эффект разницы скоростей Земли и другой планеты, как когда поезд обгоняет другой поезд и тот кажется едущим назад. В астрологии ретроградность означает, что энергия планеты направлена внутрь, а не наружу. Это не «поломка» — это другой режим работы.',
      },
      {
        heading: 'Ретроградные планеты в натальной карте',
        text: 'Если планета была ретроградной в момент вашего рождения, её энергия проявляется более интровертированно, глубоко и нестандартно. Ретроградный Меркурий — нестандартное мышление, глубокий анализ, но возможные сложности с коммуникацией. Ретроградная Венера — необычные ценности в любви, поздняя любовь, глубокие чувства. Ретроградный Марс — скрытая энергия, нестандартные методы действия. Около 80% людей имеют хотя бы одну ретроградную планету в карте — это абсолютно нормально.',
      },
      {
        heading: 'Ретроградный Меркурий в транзитах',
        text: 'Самый известный транзит. Меркурий становится ретроградным 3–4 раза в год на ~3 недели. В эти периоды чаще случаются сбои в коммуникации, технике, транспорте и документах. Рекомендуется: перепроверять информацию, делать резервные копии, не подписывать важные контракты без тщательной проверки. НЕ рекомендуется: впадать в панику и отменять всю жизнь. Миллионы людей успешно работают, путешествуют и заключают сделки в эти периоды.',
      },
      {
        heading: 'Ретроградность других планет',
        text: 'Венера ретроградная (раз в 1,5 года, ~40 дней) — пересмотр отношений и ценностей, возвращение бывших. Марс ретроградный (раз в 2 года, ~2,5 месяца) — снижение энергии, пересмотр целей и методов действия. Юпитер, Сатурн, Уран, Нептун, Плутон ретроградны по несколько месяцев в году — их ретроградность менее заметна в повседневной жизни, но важна для внутренней работы.',
      },
    ],
    keyFacts: [
      'Ретроградность — оптическая иллюзия, планеты не движутся назад',
      'Около 80% людей имеют ретроградные планеты в карте — это нормально',
      'Ретроградный Меркурий бывает 3–4 раза в год по ~3 недели',
      'Ретроградная энергия направлена внутрь — это не «поломка», а другой режим',
      'Внешние планеты ретроградны по несколько месяцев ежегодно',
    ],
    commonMyths: [
      '«Ретроградный Меркурий — катастрофа» — это просто период повышенного внимания к деталям',
      '«Нельзя ничего начинать» — можно, просто перепроверяйте информацию',
      '«Ретроградные планеты в карте — это плохо» — это просто другой стиль проявления энергии',
    ],
    tip: 'Проверьте, есть ли в вашей карте ретроградные планеты. Если да — это ваша суперсила: вы видите вещи глубже и нестандартнее, чем другие.',
    relatedService: 'service-forecast',
    relatedServiceLabel: 'Узнать текущие транзиты и ретроградности',
  },

  // ═══════════════════════════════════════════
  // ПРОДВИНУТЫЕ ТЕМЫ
  // ═══════════════════════════════════════════
  {
    id: 'what-are-transits',
    title: 'Транзиты: как планеты влияют на вас сейчас',
    category: 'advanced',
    summary:
      'Транзиты — это текущее положение планет относительно вашей натальной карты. Они показывают, какие темы и события актуальны для вас прямо сейчас.',
    sections: [
      {
        heading: 'Что такое транзиты',
        text: 'Транзиты — это движение планет в реальном времени и их взаимодействие с вашей натальной картой. Когда транзитная планета проходит по чувствительной точке вашей карты (планете, Асцендент, MC), она «активирует» эту точку и запускает определённые процессы в вашей жизни. Транзиты — это главный инструмент прогностической астрологии.',
      },
      {
        heading: 'Быстрые vs медленные транзиты',
        text: 'Транзиты Луны длятся несколько часов и влияют на настроение. Транзиты Солнца, Меркурия, Венеры — несколько дней, мелкие события. Транзиты Марса — несколько недель, энергия и конфликты. Транзиты Юпитера — около года, возможности и рост. Транзиты Сатурна — 2–3 года, серьёзные уроки и структурные изменения. Транзиты Урана, Нептуна, Плутона — несколько лет, глубокая трансформация жизни.',
      },
      {
        heading: 'Ключевые транзиты жизни',
        text: 'Возврат Сатурна (~29 лет) — главный кризис взросления, переоценка жизни. Оппозиция Урана (~42 года) — кризис середины жизни, потребность в свободе. Возврат Хирона (~50 лет) — исцеление глубинных ран. Второй возврат Сатурна (~58 лет) — мудрость и принятие. Возврат Урана (~84 года) — полный цикл обновления. Эти транзиты универсальны — их проходят все люди примерно в одном возрасте.',
      },
    ],
    keyFacts: [
      'Транзиты — главный инструмент астрологического прогноза',
      'Медленные транзиты (Сатурн, Уран, Нептун, Плутон) — самые значимые',
      'Возврат Сатурна (~29 лет) — один из важнейших транзитов жизни',
      'Транзит действует не один день, а в течение всего периода точного аспекта',
      'Один и тот же транзит проявляется по-разному в зависимости от натальной карты',
    ],
    commonMyths: [
      '«Транзит = конкретное событие в конкретный день» — транзит это период, а не точка',
      '«Плохие транзиты приносят только плохое» — сложные транзиты часто дают прорывы',
      '«Можно избежать транзита» — нет, но можно осознанно с ним работать',
    ],
    tip: 'Закажите прогноз транзитов на год — вы будете знать заранее, какие периоды благоприятны для начинаний, а какие лучше использовать для внутренней работы.',
    relatedService: 'service-forecast',
    relatedServiceLabel: 'Заказать прогноз транзитов',
  },
  {
    id: 'what-is-solar-return',
    title: 'Солярный возврат (Solar Return)',
    category: 'advanced',
    summary:
      'Солярный возврат — это карта вашего «астрологического дня рождения». Она показывает главные темы и события предстоящего года жизни.',
    sections: [
      {
        heading: 'Что это такое',
        text: 'Солярный возврат (Solar Return) — это момент, когда транзитное Солнце возвращается точно на то же место, где оно было в момент вашего рождения. Это происходит раз в год, обычно в день рождения (±1 день). Карта, построенная на этот момент, называется соляром и показывает основные темы, события и настроение предстоящего года — от одного дня рождения до следующего.',
      },
      {
        heading: 'Как читать соляр',
        text: 'Ключевые элементы соляра: Асцендент соляра — общий тон года. Положение Солнца по дому — главная сфера фокуса. Луна соляра — эмоциональный фон года. Планеты в угловых домах (1, 4, 7, 10) — самые яркие события. Аспекты к натальным планетам — как год взаимодействует с вашей базовой картой. Соляр читается вместе с натальной картой, а не отдельно.',
      },
      {
        heading: 'Практическое применение',
        text: 'Соляр помогает подготовиться к году: понять, какие сферы будут активны, где ждать возможностей, а где — вызовов. Некоторые астрологи практикуют «релокацию соляра» — встречают день рождения в другом городе, чтобы изменить Асцендент соляра и скорректировать темы года. Это спорная практика, но многие отмечают её эффективность.',
      },
    ],
    keyFacts: [
      'Соляр строится на момент точного возврата Солнца — обычно в день рождения ±1 день',
      'Показывает темы года от дня рождения до дня рождения',
      'Асцендент соляра — общий тон года, Солнце по дому — главный фокус',
      'Читается вместе с натальной картой, а не изолированно',
      'Можно «релоцировать» соляр, встретив день рождения в другом городе',
    ],
    commonMyths: [
      '«Соляр заменяет натальную карту» — нет, он дополняет её, показывая темы конкретного года',
      '«Плохой соляр = плохой год» — соляр показывает темы, а не «хорошо/плохо»',
      '«Релокация соляра — это обман» — многие астрологи подтверждают её эффективность на практике',
    ],
    tip: 'Закажите соляр за 1–2 месяца до дня рождения — так вы сможете подготовиться к темам нового года и использовать его энергию максимально эффективно.',
    relatedService: 'service-solar',
    relatedServiceLabel: 'Заказать отчёт по солярному возврату',
  },
  {
    id: 'what-is-synastry',
    title: 'Синастрия: астрология совместимости',
    category: 'advanced',
    summary:
      'Синастрия — это сравнение двух натальных карт для анализа совместимости. Узнайте, как астрология помогает понять отношения.',
    sections: [
      {
        heading: 'Что такое синастрия',
        text: 'Синастрия (от греч. «совместное влияние звёзд») — это метод сравнения двух натальных карт для анализа отношений между людьми. Астролог накладывает одну карту на другую и смотрит, как планеты одного человека взаимодействуют с планетами другого. Это позволяет увидеть зоны притяжения, конфликтов, взаимного роста и кармических связей.',
      },
      {
        heading: 'Ключевые факторы совместимости',
        text: 'Солнце–Луна: эмоциональная совместимость, базовое понимание. Венера–Марс: сексуальная и романтическая химия. Луна–Луна: эмоциональный комфорт, бытовая совместимость. Сатурн в синастрии: стабильность, но и ограничения. Плутон в синастрии: трансформация, страсть, но и борьба за власть. Юпитер: взаимный рост и поддержка. Важно: нет «идеальной» совместимости. Любые отношения требуют работы.',
      },
      {
        heading: 'Композитная карта',
        text: 'Помимо синастрии существует композитная карта — это карта самих отношений (не двух людей, а их союза). Она строится как средняя точка между одноимёнными планетами двух карт. Композит показывает, какой «третий организм» создают два человека вместе — его характер, цели и вызовы. Это особенно полезно для понимания долгосрочных отношений и брака.',
      },
    ],
    keyFacts: [
      'Синастрия сравнивает две натальные карты для анализа отношений',
      'Ключевые аспекты: Солнце–Луна, Венера–Марс, контакты с Сатурном',
      'Композитная карта — это карта самих отношений как «третьего организма»',
      'Нет «идеальной совместимости» — важен баланс гармонии и напряжения',
      'Синастрия работает для любых отношений: романтических, деловых, дружеских',
    ],
    commonMyths: [
      '«Овен и Рак несовместимы» — совместимость по солнечным знакам это 5% реальной картины',
      '«Много квадратур = плохие отношения» — квадратуры дают страсть и динамику',
      '«Астрология может предсказать развод» — она показывает вызовы, а решения принимают люди',
    ],
    tip: 'Закажите анализ совместимости — вы узнаете не только «подходите ли вы друг другу», но и конкретные зоны роста, конфликтов и способы их проработки.',
    relatedService: 'service-compatibility',
    relatedServiceLabel: 'Заказать анализ совместимости',
  },
  {
    id: 'lunar-nodes',
    title: 'Лунные узлы: кармический путь',
    category: 'advanced',
    summary:
      'Северный и Южный лунные узлы показывают вашу кармическую задачу — откуда вы пришли и куда должны двигаться в этой жизни.',
    sections: [
      {
        heading: 'Что такое лунные узлы',
        text: 'Лунные узлы — это точки пересечения орбиты Луны с эклиптикой (видимым путём Солнца). Это не физические тела, а математические точки, но в астрологии они имеют огромное значение. Северный узел (Раху) показывает направление вашего развития — куда вы должны двигаться. Южный узел (Кету) показывает ваш прошлый опыт — откуда вы пришли и что уже умеете.',
      },
      {
        heading: 'Северный узел — ваша миссия',
        text: 'Северный узел — это зона дискомфорта и роста. Качества знака и дома, где он стоит, даются вам с трудом, но именно в этом направлении лежит ваше развитие. Например, Северный узел в 10 доме говорит о необходимости строить карьеру и брать ответственность, даже если вам хочется спрятаться дома (Южный узел в 4 доме). Движение к Северному узлу всегда требует усилий, но приносит глубокое удовлетворение.',
      },
      {
        heading: 'Южный узел — ваш багаж',
        text: 'Южный узел показывает то, что вы уже освоили (в прошлых жизнях или в раннем детстве). Это ваша зона комфорта, но и ловушка. Если вы остаётесь только в энергии Южного узла, жизнь становится стагнацией. Задача — использовать навыки Южного узла как фундамент для движения к Северному. Не отказываться от прошлого, а опираться на него для роста.',
      },
    ],
    keyFacts: [
      'Лунные узлы — это математические точки, а не физические тела',
      'Северный узел = направление роста, Южный = зона комфорта',
      'Узлы всегда находятся в противоположных знаках и домах',
      'Цикл узлов — ~18,6 лет (возврат узлов в 18–19, 37–38, 56–57 лет)',
      'Затмения происходят вблизи лунных узлов и усиливают их тему',
    ],
    commonMyths: [
      '«Южный узел — это плохо» — это ваш ценный опыт и ресурс, не проклятие',
      '«Нужно полностью отказаться от Южного узла» — нет, нужен баланс с опорой на рост',
      '«Лунные узлы доказывают реинкарнацию» — это символическая интерпретация, не доказательство',
    ],
    tip: 'Узнайте положение своих лунных узлов — это одна из самых глубоких и трансформирующих тем в астрологии. Многие люди говорят, что именно узлы объяснили им смысл их жизненного пути.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнать свои лунные узлы',
  },

  // ═══════════════════════════════════════════
  // НАШИ УСЛУГИ
  // ═══════════════════════════════════════════
  {
    id: 'service-natal-report',
    title: 'Натальная карта: наш отчёт',
    category: 'services',
    summary:
      'Полный автоматический отчёт по вашей натальной карте — детальный разбор всех планет, домов, аспектов и рекомендации для жизни.',
    sections: [
      {
        heading: 'Что вы получите',
        text: 'Наш отчёт по натальной карте — это 20–30 страниц детального анализа вашей личности. Вы получите разбор положения каждой планеты в знаке и доме, анализ всех значимых аспектов, описание вашей «большой тройки» (Солнце, Луна, Асцендент), характеристику стихийного и модального баланса, а также практические рекомендации по каждой сфере жизни.',
      },
      {
        heading: 'Разделы отчёта',
        text: 'Отчёт включает: общий портрет личности, эмоциональный мир (Луна), стиль мышления (Меркурий), любовь и ценности (Венера), энергия и действие (Марс), карьера и амбиции (Сатурн, MC), духовность и трансформация (высшие планеты), кармические задачи (лунные узлы). Каждый раздел содержит описание, сильные стороны, вызовы и конкретные советы.',
      },
      {
        heading: 'Для кого подходит',
        text: 'Отчёт подходит как для новичков в астрологии (всё написано понятным языком), так и для продвинутых — вы найдёте глубокий анализ аспектов и конфигураций. Это идеальный подарок себе или близким на день рождения. Отчёт генерируется автоматически на основе точных астрономических расчётов и профессиональных интерпретаций.',
      },
    ],
    keyFacts: [
      '20–30 страниц детального анализа',
      'Разбор всех 10 планет в знаках и домах',
      'Анализ ключевых аспектов с практическими советами',
      'Понятный язык — подходит и новичкам, и продвинутым',
      'Генерируется мгновенно после ввода данных рождения',
    ],
    commonMyths: [
      '«Автоматический отчёт хуже консультации» — наш отчёт основан на тех же принципах, что используют профессионалы',
      '«Все онлайн-отчёты одинаковые» — наш отчёт учитывает аспекты, дома и конфигурации, а не только знаки',
      '«Достаточно бесплатного гороскопа» — бесплатные гороскопы учитывают 1 фактор из 50+',
    ],
    tip: 'Для максимальной точности укажите время рождения с точностью до минуты. Если не знаете — укажите примерное, но отметьте это в комментарии.',
    relatedService: 'service-natal',
    relatedServiceLabel: 'Заказать натальную карту',
  },
  {
    id: 'service-compatibility-report',
    title: 'Анализ совместимости: наш отчёт',
    category: 'services',
    summary:
      'Детальный отчёт о совместимости двух людей — романтической, эмоциональной, интеллектуальной и сексуальной.',
    sections: [
      {
        heading: 'Что вы получите',
        text: 'Отчёт по совместимости сравнивает две натальные карты и анализирует все ключевые взаимодействия. Вы узнаете, в чём ваша сила как пары, где скрыты конфликты, как улучшить коммуникацию и какие сферы жизни будут наиболее гармоничными. Отчёт включает анализ эмоциональной, интеллектуальной, сексуальной и бытовой совместимости.',
      },
      {
        heading: 'Методология',
        text: 'Мы используем классическую синастрию (наложение карт) и анализируем: аспекты между личными планетами (Солнце, Луна, Меркурий, Венера, Марс), положение планет одного партнёра в домах другого, контакты с Сатурном (стабильность), Плутоном (трансформация) и Юпитером (рост). Каждый аспект получает оценку и практический комментарий.',
      },
      {
        heading: 'Для кого подходит',
        text: 'Отчёт подходит для романтических пар (новых и давних), для деловых партнёров, для родителей и детей, для друзей. Любые отношения между двумя людьми можно проанализировать через синастрию. Это отличный инструмент для улучшения понимания и коммуникации.',
      },
    ],
    keyFacts: [
      'Сравнение двух полных натальных карт',
      'Анализ эмоциональной, интеллектуальной и сексуальной совместимости',
      'Конкретные рекомендации по улучшению отношений',
      'Подходит для любых типов отношений',
      'Нужны данные рождения обоих партнёров',
    ],
    commonMyths: [
      '«Если совместимость низкая — нужно расставаться» — отчёт показывает зоны роста, а не приговор',
      '«Совместимость по знакам Солнца достаточна» — это лишь 5% реальной картины',
      '«Идеальная совместимость существует» — все отношения имеют и гармонию, и вызовы',
    ],
    tip: 'Закажите отчёт вместе с партнёром и обсудите результаты — это может стать отличным поводом для глубокого разговора о ваших отношениях.',
    relatedService: 'service-compatibility',
    relatedServiceLabel: 'Заказать анализ совместимости',
  },
  {
    id: 'service-forecast-report',
    title: 'Прогноз транзитов: наш отчёт',
    category: 'services',
    summary:
      'Персональный астрологический прогноз на основе транзитов — узнайте, какие периоды благоприятны для вас, а какие требуют осторожности.',
    sections: [
      {
        heading: 'Что вы получите',
        text: 'Отчёт по транзитам анализирует движение всех планет относительно вашей натальной карты на выбранный период (3, 6 или 12 месяцев). Вы получите помесячный разбор ключевых транзитов с датами, описанием влияния и практическими рекомендациями. Это ваш персональный астрологический планировщик.',
      },
      {
        heading: 'Что анализируется',
        text: 'Мы отслеживаем транзиты Юпитера (возможности, рост), Сатурна (уроки, структура), Урана (перемены, свобода), Нептуна (духовность, иллюзии), Плутона (трансформация, власть), а также затмения и ретроградности. Для каждого транзита указаны точные даты, затронутые сферы жизни и конкретные советы.',
      },
      {
        heading: 'Практическое применение',
        text: 'Используйте отчёт для планирования важных решений: когда лучше начинать новый проект, менять работу, вступать в отношения, путешествовать. Знание транзитов не делает вас заложником звёзд — оно даёт вам преимущество осознанности. Вы понимаете, какие энергии действуют, и можете использовать их в свою пользу.',
      },
    ],
    keyFacts: [
      'Персональный прогноз на 3, 6 или 12 месяцев',
      'Помесячный разбор с точными датами транзитов',
      'Анализ транзитов всех медленных планет + затмения',
      'Практические рекомендации для каждого периода',
      'Основан на вашей индивидуальной натальной карте',
    ],
    commonMyths: [
      '«Прогноз предсказывает конкретные события» — он показывает энергии и тенденции, а не факты',
      '«Астрологический прогноз = газетный гороскоп» — персональный прогноз учитывает всю вашу карту',
      '«Если прогноз плохой, ничего нельзя сделать» — осознанность позволяет работать с любой энергией',
    ],
    tip: 'Закажите прогноз на год вперёд — это лучшая инвестиция в осознанное планирование. Вы будете знать, когда действовать, а когда ждать.',
    relatedService: 'service-forecast',
    relatedServiceLabel: 'Заказать прогноз транзитов',
  },
  {
    id: 'service-solar-report',
    title: 'Солярный возврат: наш отчёт',
    category: 'services',
    summary:
      'Отчёт по солярному возврату — узнайте главные темы и события вашего предстоящего года от дня рождения до дня рождения.',
    sections: [
      {
        heading: 'Что вы получите',
        text: 'Отчёт по солярному возврату анализирует карту вашего «астрологического нового года» — момента, когда Солнце возвращается на своё натальное положение. Вы узнаете главные темы года, ключевые сферы активности, эмоциональный фон и потенциальные вызовы. Это ваш персональный «прогноз на год».',
      },
      {
        heading: 'Что анализируется',
        text: 'Асцендент соляра (общий тон года), Солнце по дому (главный фокус), Луна соляра (эмоциональный фон), планеты в угловых домах (яркие события), аспекты соляра к натальной карте (как год взаимодействует с вашей базовой энергией). Каждый элемент получает подробную интерпретацию.',
      },
      {
        heading: 'Когда заказывать',
        text: 'Идеально — за 1–2 месяца до дня рождения. Так вы сможете подготовиться к темам нового года и осознанно войти в новый цикл. Отчёт также полезен, если год уже начался — вы поймёте, почему определённые темы стали актуальными.',
      },
    ],
    keyFacts: [
      'Анализ карты на момент точного возврата Солнца',
      'Прогноз на год — от дня рождения до дня рождения',
      'Ключевые темы, возможности и вызовы года',
      'Рекомендации по каждой сфере жизни',
      'Лучше заказывать за 1–2 месяца до дня рождения',
    ],
    commonMyths: [
      '«Соляр работает только если верить» — это математический расчёт, не вопрос веры',
      '«Один год не может отличаться от другого» — каждый год Солнце возвращается в разную конфигурацию планет',
      '«Соляр заменяет натальную карту» — нет, он дополняет её, показывая темы конкретного года',
    ],
    tip: 'Сравните соляры прошлых лет с реальными событиями — вы удивитесь точности совпадений. Это лучший способ убедиться в работоспособности метода.',
    relatedService: 'service-solar',
    relatedServiceLabel: 'Заказать отчёт по солярному возврату',
  },
  {
    id: 'service-combo-report',
    title: 'Комбо-анализ: полный разбор',
    category: 'services',
    summary:
      'Максимально полный астрологический анализ — натальная карта + транзиты + соляр в одном отчёте со скидкой.',
    sections: [
      {
        heading: 'Что включено',
        text: 'Комбо-анализ объединяет три наших ключевых отчёта в один: полная натальная карта (кто вы), прогноз транзитов на год (что вас ждёт) и солярный возврат (темы года). Вместе они дают максимально полную картину — вы понимаете и свою базовую природу, и текущие тенденции, и главные темы предстоящего года.',
      },
      {
        heading: 'Преимущества комбо',
        text: 'Главное преимущество — синтез. Когда астролог (или алгоритм) видит все три карты одновременно, он может делать более точные и глубокие выводы. Например, транзит Сатурна по вашему Солнцу в сочетании с соляром, где Сатурн в 10 доме, однозначно указывает на серьёзные карьерные изменения. По отдельности каждый фактор менее информативен.',
      },
      {
        heading: 'Для кого подходит',
        text: 'Комбо-анализ идеален для тех, кто хочет получить максимум информации за один раз. Это также отличный подарок — получатель получает полный астрологический портрет и прогноз. Экономия по сравнению с заказом трёх отчётов по отдельности составляет 20–30%.',
      },
    ],
    keyFacts: [
      '3 отчёта в одном: натальная карта + транзиты + соляр',
      'Синтез даёт более точные и глубокие выводы',
      'Экономия 20–30% по сравнению с отдельными заказами',
      'Идеальный подарок на день рождения',
      'Самый полный астрологический анализ на нашей платформе',
    ],
    commonMyths: [
      '«Лучше заказать по отдельности» — комбо даёт синтез, который невозможен при раздельном анализе',
      '«Слишком много информации» — отчёт структурирован по разделам, читайте по частям',
      '«Дорого» — это самый выгодный вариант по соотношению цена/объём',
    ],
    tip: 'Закажите комбо-анализ к своему дню рождения — вы получите и глубокое понимание себя, и чёткий план на предстоящий год.',
    relatedService: 'service-combo',
    relatedServiceLabel: 'Заказать комбо-анализ',
  },
]
